import { useState, useEffect } from "react";
import { classNames } from "primereact/utils";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import {
  ForumTopicEnum,
  ForumWithoutMessage,
  getColorForLetter,
  ToggleVisibilityDto
} from "../../services/forum/forum.dto";
import forumService from "../../services/forum/forum.service";
import { useToast } from "../../utils/toast";
import { DataView } from "primereact/dataview";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Fieldset } from "primereact/fieldset";
import { CardDataDto, cardData } from "./ForumHomePage";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../utils/auth";
import { Role } from "../../services/auth/auth.dto";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { BreadCrumb } from "primereact/breadcrumb";
import { MenuItem } from "primereact/menuitem";
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';

interface Mesaj {
  mesaj: string;
  mesaj_kimden: string;
}

interface Topic {
  forum_header: string;
  message_content: string;
  forum_topic: ForumTopicEnum;
  mesajlar: Mesaj[];
  creatorName: string;
  creation_date: string;
  view_count: number;
  replies: number;
}

const ForumlarPage = () => {
  const { topicId } = useParams<{ topicId: string }>();
  const location = useLocation();  
  const [searchTerm, setSearchTerm] = useState(location.state?.searchTerm || "");
  const topicIdNumber = Number(topicId);
  const toast = useToast();
  const navigate = useNavigate();
  const [selectedCard, setSelectedCard] = useState<CardDataDto>(cardData[0]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const auth = useAuth();
  const items: MenuItem[] = [
    {
      label: "Forum",
      command: () => {
        navigate("/app/forum-homepage");
      }
    },
    { label: cardData[topicIdNumber - 1].title }
  ];
  const home: MenuItem = {
    icon: "pi pi-home",
    command: () => {
      navigate("/app/forum-homepage");
    }
  };

  const [sortField, setSortField] = useState('creation-date');
  const [sortOrder, setSortOrder] = useState(1);
  const [sortOption, setSortOption] = useState('creation-date_asc');

  const sortOptions = [
    { label: 'En Yeni', value: 'creation-date_asc' },
    { label: 'En Eski', value: 'creation-date_desc' },
    { label: 'En Çok Yanıt', value: 'replies_desc' },
    { label: 'En Çok Görüntülenen', value: 'view-count_asc' }
  ];

  const onSortChange = (event: { value: string }) => {
    const selectedValue = event.value;
    setSortOption(selectedValue);
    
    const [field, direction] = selectedValue.split('_');
    setSortField(field);
    setSortOrder(direction === 'asc' ? 1 : -1);
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { data: ForumPageResponse, refetch: refetchForumPage, isLoading: forumPageResponseLoading } = useQuery({
    queryKey: ["ForumPage", topicId],
    refetchOnMount: false,
    queryFn: async () => {
      if (topicId) {
        setSelectedCard(cardData[topicIdNumber - 1]);
        return forumService.GetForumByTopic(topicId);
      }
      throw new Error("Invalid topic");
    }
  });

  const formatDateTime = (dateString: Date) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Geçersiz tarih";
      }
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error("Tarih formatlanırken hata oluştu:", error);
      return "Geçersiz tarih";
    }
  };

  const itemTemplate = (forum: ForumWithoutMessage, index: number) => {
    const firstLetter = forum.creator_name.charAt(0).toUpperCase();
    const bgColor = getColorForLetter(firstLetter);

    const handleClick = () => {
      navigate(`/app/forum-ayrinti/${forum.id}/${topicIdNumber}`);
    };

    const handleDelete = async (e: React.MouseEvent) => {
      try {
        confirmPopup({
          target: e.target as HTMLElement,
          message: "Bu işlem geri alınamaz. Konuyu silmek istediğinizden emin misiniz?",
          icon: "pi pi-info-circle",
          defaultFocus: "accept",
          acceptClassName: "p-button-success",
          acceptLabel: "Onayla",
          rejectLabel: "İptal",
          accept: async () => {
            const toggleVisibilityDto: ToggleVisibilityDto = { visibility: false };
            forumService.ToggleVisibility(forum.id, toggleVisibilityDto);
          },
          reject: () => {}
        });

        //refetchForumPage(); // Refresh the list after deletion
      } catch (error) {
        toast.show("Konu silinirken bir hata oluştu", "error");
      }
    };

    return (
      <div className="col-12" key={forum.id}>
        <div
          className={classNames("flex flex-row xl:align-items-start p-0 xl:p-1 gap-2 xl:gap-4", {
            "border-top-1 surface-border": index !== 0
          })}
          onClick={handleClick}
        >
          {/* Başlık İkonu */}
          <div
            className="w-3rem h-3rem flex justify-content-center align-items-center text-white text-xl font-bold border-circle shadow-2 my-1"
            style={{ backgroundColor: bgColor }}
          >
            <div className="flex align-items-center justify-content-center" style={{ height: "100%" }}>
              {firstLetter}
            </div>
          </div>

          {/* İçerik */}
          <div className="flex flex-column sm:flex-row justify-content-between align-items-center xl:align-items-start flex-1 gap-4">
            {/* Sol İçerik */}
            <div className="flex flex-column gap-2">
              <div className="text-md xl:text-lg font-bold text-900">{forum.forum_header}</div>

              <div className="flex align-items-center gap-1 text-xs xl:text-sm text-600">
                <i className="pi pi-user" style={{ verticalAlign: "middle" }}></i>
                <span>{forum.creator_name}</span>
                <span>-</span>
                <i className="pi pi-calendar" style={{ verticalAlign: "middle" }}></i>
                <span>{forum.creation_date ? formatDateTime(forum.creation_date) : "Tarih yok"}</span>
              </div>

              {windowWidth < 768 && (
                <div className="flex align-items-center gap-1 text-xs xl:text-sm text-600">
                  <i className="pi pi-comments " style={{ verticalAlign: "middle" }}></i>
                  <span>{forum.replies} cevap</span>
                  {auth.getUser().role === Role.ADMIN && (
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      text
                      onClick={handleDelete}
                      className="p-button-rounded"
                      tooltip="Sil"
                    />
                  )}
                </div>
              )}
            </div>

            {/* Sağ İçerik */}
            <div className="flex flex-row gap-2 justify-content-end align-items-end">
              {/* Masaüstü Görünüm */}
              {windowWidth > 768 && (
                <>
                  <div className="flex flex-column gap-2 justify-content-end align-items-end">
                    {/* Cevap Sayısı */}
                    <div className="text-sm text-600 flex items-center">
                      <i className="pi pi-comments mr-2" style={{ verticalAlign: "middle" }}></i>
                      {forum.replies} cevap
                    </div>

                    {/* Görüntülenme Sayısı */}
                    <div className="text-sm text-600 flex items-center">
                      <i className="pi pi-eye mr-2" style={{ verticalAlign: "middle" }}></i>
                      {forum.view_count} görüntülenme
                    </div>
                  </div>

                  {/* Sil Butonu */}
                  {auth.getUser().role === Role.ADMIN && (
                    <Button
                      icon="pi pi-trash"
                      severity="danger"
                      text
                      onClick={e => {
                        e.stopPropagation();
                        handleDelete(e);
                      }}
                      className="p-button-rounded"
                      tooltip="Sil"
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getFilteredAndSortedForums = () => {
    if (!ForumPageResponse?.result?.data) return [];

    let filteredData = [...ForumPageResponse.result.data];

    // Arama filtrelemesi
    if (searchTerm) {
      filteredData = filteredData.filter(forum =>
        forum.forum_header.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sıralama
    return filteredData.sort((a: any, b: any) => {
      if (sortField === 'creation-date') {
        const dateA = new Date(a.creation_date).getTime();
        const dateB = new Date(b.creation_date).getTime();
        return (dateB - dateA) * (sortOrder);
      } else if (sortField === 'view-count'  ){
        return (b["view_count"] - a["view_count"]) * (sortOrder);
      }else if ( sortField === 'replies') {
        return (b[sortField] - a[sortField]) * (sortOrder);
      }
      return ((a[sortField] - b[sortField]) * sortOrder);
    });
  };

  return (
    <div className="">
      <BreadCrumb className="mb-0 lg:mb-3 mx-0 lg:mx-2" model={items} home={home} />
      <ConfirmPopup />
      
      {/* Üst Toolbar */}
      <div className="flex flex-column lg:flex-row gap-3 mb-0 lg:mb-3 mx-0 lg:mx-2">
        {/* Sol Grup - Arama */}
        <div className="flex-1">
          <div className="p-inputgroup">
            <span className="p-inputgroup-addon">
              <i className="pi pi-search"></i>
            </span>
            <InputText
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Forum Ara..."
              className="w-full"
            />
            {searchTerm && (
              <Button
                icon="pi pi-times"
                className="p-button-outlined p-button-secondary"
                onClick={() => setSearchTerm("")}
                tooltip="Aramayı Temizle"
              />
            )}
          </div>
        </div>

        {/* Orta Grup - Sıralama */}
        <div className="flex align-items-center">
          <Dropdown
            options={sortOptions}
            value={sortOption}
            onChange={onSortChange}
            className="w-full lg:w-15rem"
            placeholder="Sıralama Seçin"
          />
        </div>

        {/* Sağ Grup - Yeni Konu */}
        <div className="flex">
          <Button 
            label="Yeni Konu Aç" 
            icon="pi pi-plus"
            className="w-full lg:w-auto" 
            onClick={() => navigate("/app/forum-ekle")} 
          />
        </div>
      </div>

      {/* Forum Listesi */}
      <div className="card p-4 mb-4 mx-0 lg:mx-2">
        <div className="">
          <Fieldset legend={selectedCard.title} className="mb-4 shadow-lg border-round">
            <div className="text-black-alpha-90 text-xl text-base mb-4">{selectedCard.description}</div>

            <DataView
              value={getFilteredAndSortedForums()}
              itemTemplate={itemTemplate as any}
              layout="list"
              paginator
              rows={10}
              emptyMessage="Listelenecek konu bulunamadı."
            />
          </Fieldset>
        </div>
      </div>
    </div>
  );
};

export default ForumlarPage;
